/**
 * LaTeX Context Parser
 * Detects current context: math mode, environments, packages, etc.
 */

import { LaTeXContext, ContextMode } from './types'
import { logDebug } from '@utils/logger'

/**
 * Parse LaTeX context from document text and cursor position
 */
export function parseContext(text: string, position: number): LaTeXContext {
  const context: LaTeXContext = {
    mode: ContextMode.NORMAL,
    depth: 0,
    inMathMode: false,
    inDisplayMath: false,
    recentCommands: [],
    packages: [],
    position
  }
  
  // Parse preamble for packages
  const preambleEnd = text.indexOf('\\begin{document}')
  if (preambleEnd > 0) {
    const preamble = text.substring(0, preambleEnd)
    context.packages = extractPackages(preamble)
  }
  
  // Analyze text before cursor to determine context
  const textBefore = text.substring(0, position)
  
  // Check for math mode
  const mathContext = detectMathMode(textBefore)
  context.inMathMode = mathContext.inMathMode
  context.inDisplayMath = mathContext.inDisplayMath
  context.mode = mathContext.mode
  
  // Check for current environment
  const envInfo = detectCurrentEnvironment(textBefore)
  context.environment = envInfo.name
  context.depth = envInfo.depth
  
  // Extract recent commands
  context.recentCommands = extractRecentCommands(textBefore)
  
  logDebug('[ContextParser] Parsed context', {
    mode: context.mode,
    environment: context.environment,
    inMathMode: context.inMathMode,
    packages: context.packages.length
  })
  
  return context
}

/**
 * Detect if we're in math mode
 */
function detectMathMode(text: string): { 
  inMathMode: boolean
  inDisplayMath: boolean
  mode: ContextMode 
} {
  // Count unescaped $ signs
  let dollarCount = 0
  let displayMathCount = 0
  let inVerbatim = false
  let inComment = false
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const prevChar = i > 0 ? text[i - 1] : ''
    
    // Check for verbatim
    if (text.substring(i, i + 9) === '\\begin{verbatim}') {
      inVerbatim = true
      i += 8
      continue
    }
    if (inVerbatim && text.substring(i, i + 7) === '\\end{verbatim}') {
      inVerbatim = false
      i += 6
      continue
    }
    
    // Check for comments
    if (char === '%' && prevChar !== '\\' && !inVerbatim) {
      inComment = true
      continue
    }
    if (char === '\n' && inComment) {
      inComment = false
      continue
    }
    
    if (inVerbatim || inComment) continue
    
    // Count $ for inline math
    if (char === '$' && prevChar !== '\\') {
      dollarCount++
    }
    
    // Check for display math \[ or \]
    if (text.substring(i, i + 2) === '\\[') {
      displayMathCount++
      i++
    } else if (text.substring(i, i + 2) === '\\]') {
      displayMathCount--
      i++
    }
    
    // Check for inline math \( or \)
    if (text.substring(i, i + 2) === '\\(') {
      dollarCount++
      i++
    } else if (text.substring(i, i + 2) === '\\)') {
      dollarCount--
      i++
    }
  }
  
  const inMathMode = dollarCount % 2 === 1
  const inDisplayMath = displayMathCount > 0
  
  let mode = ContextMode.NORMAL
  if (inDisplayMath) {
    mode = ContextMode.MATH_DISPLAY
  } else if (inMathMode) {
    mode = ContextMode.MATH_INLINE
  }
  
  return { inMathMode, inDisplayMath, mode }
}

/**
 * Detect current environment and nesting depth
 */
function detectCurrentEnvironment(text: string): { name?: string; depth: number } {
  const environments: string[] = []
  let depth = 0
  let inVerbatim = false
  
  // Scan backwards for \begin and \end
  for (let i = text.length - 1; i >= 0; i--) {
    // Check for verbatim (skip parsing inside)
    if (text.substring(i - 6, i + 1) === 'verbatim') {
      if (text.substring(i - 12, i + 1) === '\\begin{verbatim}') {
        inVerbatim = true
        i -= 12
        continue
      } else if (text.substring(i - 10, i + 1) === '\\end{verbatim}') {
        inVerbatim = false
        i -= 10
        continue
      }
    }
    
    if (inVerbatim) continue
    
    // Check for \end{env}
    if (text.substring(i - 4, i + 1) === '\\end{') {
      const envEnd = text.indexOf('}', i + 1)
      if (envEnd > i) {
        const envName = text.substring(i + 1, envEnd)
        environments.push(envName)
        depth++
        i = i - 4
        continue
      }
    }
    
    // Check for \begin{env}
    if (text.substring(i - 6, i + 1) === '\\begin{') {
      const envEnd = text.indexOf('}', i + 1)
      if (envEnd > i) {
        const envName = text.substring(i + 1, envEnd)
        if (environments.length > 0 && environments[environments.length - 1] === envName) {
          environments.pop()
          depth--
        }
        i = i - 6
        continue
      }
    }
  }
  
  return {
    name: environments.length > 0 ? environments[environments.length - 1] : undefined,
    depth
  }
}

/**
 * Extract loaded packages from preamble
 */
function extractPackages(preamble: string): string[] {
  const packages: string[] = []
  const usepackageRegex = /\\usepackage(?:\[[^\]]*\])?\{([^}]+)\}/g
  let match
  
  while ((match = usepackageRegex.exec(preamble)) !== null) {
    const packageList = match[1].split(',').map(p => p.trim())
    packages.push(...packageList)
  }
  
  return packages
}

/**
 * Extract recent commands (last 10)
 */
function extractRecentCommands(text: string): string[] {
  const commands: string[] = []
  const commandRegex = /\\([a-zA-Z]+(?:\*)?)/g
  let match
  
  while ((match = commandRegex.exec(text)) !== null) {
    commands.push(match[1])
    if (commands.length >= 10) break
  }
  
  return commands.slice(-10).reverse()
}

